import { useState } from 'react';

interface NoShowModalProps {
  bookingName: string;
  roomName: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

const PRESET_REASONS = [
  '連絡なし',
  'ゲストからキャンセル連絡',
  '到着確認できず',
  'その他',
];

export default function NoShowModal({ bookingName, roomName, onClose, onSubmit }: NoShowModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOther = selectedReason === 'その他';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason) {
      setError('理由を選択してください。');
      return;
    }
    if (isOther && !customReason.trim()) {
      setError('その他の理由を入力してください。');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const finalReason = isOther ? `その他: ${customReason.trim()}` : selectedReason;
      await onSubmit(finalReason);
    } catch {
      setError('処理中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="rounded-xl max-w-md w-full" style={{ backgroundColor: '#FFFEFA', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #DED9CC' }}>
          <h3 className="text-sm font-semibold" style={{ color: '#252824' }}>ノーショーとして記録</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors hover:bg-background-100 disabled:opacity-50"
            style={{ color: '#6B6B67' }}
            aria-label="閉じる"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="text-xs rounded-lg px-4 py-3" style={{ backgroundColor: '#F6F4EE', color: '#6B6B67' }}>
            {bookingName} · {roomName}
          </div>

          <div className="px-4 py-3 rounded-lg text-xs" style={{ backgroundColor: '#FDF2F2', color: '#A33A32', border: '1px solid #F5C6C6' }}>
            この予約をノーショー（無断不泊）として記録します。この操作は取り消せません。
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg text-xs" style={{ backgroundColor: '#FDF2F2', color: '#A33A32', border: '1px solid #F5C6C6' }}>
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-2" style={{ color: '#252824' }}>
              理由 <span style={{ color: '#A33A32' }}>*</span>
            </label>
            <div className="space-y-1.5">
              {PRESET_REASONS.map((r) => (
                <label
                  key={r}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${
                    selectedReason === r ? '' : ''
                  }`}
                  style={{
                    backgroundColor: selectedReason === r ? '#F0EDE5' : '#F6F4EE',
                    color: '#252824',
                    border: selectedReason === r ? '1px solid #A8843A' : '1px solid transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="noShowReason"
                    value={r}
                    checked={selectedReason === r}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      setError(null);
                      if (e.target.value !== 'その他') setCustomReason('');
                    }}
                    className="sr-only"
                  />
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      border: selectedReason === r ? '4px solid #A8843A' : '2px solid #C4BFB0',
                      backgroundColor: selectedReason === r ? '#FFFEFA' : 'transparent',
                    }}
                  />
                  <span className="text-sm">{r}</span>
                </label>
              ))}
            </div>
          </div>

          {isOther && (
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#252824' }}>
                詳細 <span style={{ color: '#A33A32' }}>*</span>
              </label>
              <textarea
                value={customReason}
                onChange={(e) => { setCustomReason(e.target.value); setError(null); }}
                placeholder="例: ゲストから前日にキャンセルの連絡あり"
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg resize-none focus:outline-none"
                style={{
                  backgroundColor: '#FFFEFA',
                  color: '#252824',
                  border: '1px solid #DED9CC',
                }}
                required
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#F0EDE5', color: '#252824' }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#A33A32', color: '#FFFEFA' }}
            >
              {loading && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin"></i>
                </span>
              )}
              ノーショーとして記録
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}